import { useEffect, useMemo, useReducer, useState } from 'react';
import { getAddress } from 'ethers';
import { tokenFor, getBrowserChain } from './lib/chains.js';
import { ApiClient, ApiError } from './lib/api.js';
import { createAuthClient } from './lib/auth.js';
import type { AuthClient } from './lib/auth.js';
import { encodeTransfer } from './lib/erc20.js';
import { fetchBalance } from './lib/balance.js';
import { initialSessionState, sessionReducer } from './state/session.js';
import { Login } from './components/Login.js';
import { Balance } from './components/Balance.js';
import { OnRampButton } from './components/OnRampButton.js';
import { TransferButton } from './components/TransferButton.js';
import { Receipt } from './components/Receipt.js';
import { DevModeBanner } from './components/DevModeBanner.js';
import { DebugBanner } from './components/DebugBanner.js';
import { formatTokenAmount } from './lib/formatters.js';
import type { ChainId, TokenSymbol } from '@cryptopay/core';

function readEnv(): {
  apiBaseUrl: string;
  apiKey: string;
  web3AuthClientId: string;
  forceDevWallet: boolean;
  chainId: ChainId;
  tokenSymbol: TokenSymbol;
  fiatCurrency: string;
  isProduction: boolean;
} {
  const env = import.meta.env;
  // Trim every value: Vercel's "Paste .env" feature has a recurring
  // bug where it preserves trailing whitespace / newlines from the
  // pasted block, so `VITE_DEFAULT_CHAIN` becomes `"polygon\n\n"` and
  // downstream lookups silently miss. We sanitise at the edge so the
  // rest of the app doesn't have to know about it.
  const read = (key: string, fallback = ''): string => {
    const raw = (env as unknown as Record<string, string | undefined>)[key];
    return (raw ?? fallback).trim();
  };
  return {
    // No fallback: an empty value here surfaces as a loud ApiClient
    // constructor error rather than silently aiming relative requests
    // at the widget's own Vercel host (which 405s every POST).
    apiBaseUrl: read('VITE_API_BASE_URL'),
    apiKey: read('VITE_MERCHANT_API_KEY'),
    web3AuthClientId: read('VITE_WEB3AUTH_CLIENT_ID'),
    forceDevWallet: read('VITE_FORCE_DEV_WALLET').toLowerCase() === 'true',
    chainId: (read('VITE_DEFAULT_CHAIN', 'polygon') || 'polygon') as ChainId,
    tokenSymbol: (read('VITE_DEFAULT_TOKEN', 'USDC') || 'USDC') as TokenSymbol,
    fiatCurrency: read('VITE_DEFAULT_FIAT', 'USD') || 'USD',
    isProduction: env.MODE === 'production',
  };
}

export function App() {
  const env = useMemo(readEnv, []);
  const [api] = useState(
    () => new ApiClient({ baseUrl: env.apiBaseUrl, apiKey: env.apiKey || 'cp_live_unset' }),
  );
  const [auth] = useState<AuthClient>(() =>
    createAuthClient({
      clientId: env.web3AuthClientId,
      mode: env.isProduction ? 'production' : 'development',
      forceDevWallet: env.forceDevWallet,
    }),
  );
  const [state, dispatch] = useReducer(sessionReducer, initialSessionState(env.chainId, env.tokenSymbol));
  const [merchantMaster, setMerchantMaster] = useState<string | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [restoring, setRestoring] = useState(true);

  // Web3Auth runs in `uxMode: 'redirect'` so the auth flow leaves and
  // re-enters this page on iOS Safari. On mount we ask the auth client
  // whether the SDK already holds a connected session (i.e. we just came
  // back from auth.web3auth.io). If yes, jump straight past the Login
  // screen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await auth.restoreSession();
        if (cancelled || !session) return;
        dispatch({ type: 'login-success', auth: session });
        try {
          const upsert = await api.upsertUser(session.idToken);
          if (upsert.address.toLowerCase() !== session.address.toLowerCase()) {
            dispatch({
              type: 'error',
              message: `Address mismatch: client=${session.address}, server=${upsert.address}`,
            });
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('Backend upsert skipped on restore:', err);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Session restore failed:', err);
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const token = useMemo(
    () => tokenFor(state.chainId, state.tokenSymbol),
    [state.chainId, state.tokenSymbol],
  );
  const chain = useMemo(() => getBrowserChain(state.chainId), [state.chainId]);

  useEffect(() => {
    if (!state.auth) return;
    refreshBalance();
    loadMerchantMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.auth?.address, state.chainId, state.tokenSymbol]);

  async function refreshBalance(): Promise<void> {
    if (!state.auth) return;
    setBalanceLoading(true);
    try {
      const balance = await fetchBalance(state.chainId, state.tokenSymbol, state.auth.address);
      dispatch({ type: 'balance-loaded', balance });
    } catch (err) {
      dispatch({
        type: 'error',
        message: `Bakiye okunamadı: ${err instanceof Error ? err.message : 'unknown'}`,
      });
    } finally {
      setBalanceLoading(false);
    }
  }

  async function handleLogin(email: string): Promise<void> {
    const session = await auth.login(email);
    dispatch({ type: 'login-success', auth: session });
    try {
      const upsert = await api.upsertUser(session.idToken);
      setMerchantMaster(null);
      if (upsert.address.toLowerCase() !== session.address.toLowerCase()) {
        dispatch({
          type: 'error',
          message: `Address mismatch: client=${session.address}, server=${upsert.address}`,
        });
      }
    } catch (err) {
      // Backend olmadan da geliştirici dev wallet ile UI'yi gezebilir.
      // eslint-disable-next-line no-console
      console.warn('Backend upsert skipped:', err);
    }
  }

  async function handleOnRamp(amountUsd: number): Promise<void> {
    if (!state.auth) return;
    try {
      const session = await api.createOnRampSession({
        chainId: state.chainId,
        cryptoCurrencyCode: state.tokenSymbol,
        walletAddress: state.auth.address,
        fiatCurrency: env.fiatCurrency,
        fiatAmount: amountUsd,
      });
      window.open(session.url, '_blank', 'noopener,noreferrer');
      dispatch({ type: 'on-ramp-opened' });
    } catch (err) {
      dispatch({ type: 'error', message: friendlyApiError(err, 'On-ramp oturumu açılamadı') });
    }
  }

  async function handleTransfer(): Promise<void> {
    if (!state.auth) return;
    if (state.balance <= 0n) {
      dispatch({ type: 'error', message: 'Önce bakiye yükleyin.' });
      return;
    }
    const recipient = merchantMaster ?? (await loadMerchantMaster());
    if (!recipient) {
      dispatch({ type: 'error', message: 'Merchant master cüzdanı yapılandırılmamış.' });
      return;
    }
    dispatch({ type: 'transfer-pending' });
    try {
      const data = encodeTransfer(getAddress(recipient), state.balance);
      const signedTx = await auth.signTransfer({
        chainId: state.chainId,
        to: token.address,
        data,
      });
      const result = await api.relayTransfer({
        chainId: state.chainId,
        signedTx,
      });
      dispatch({ type: 'transfer-confirmed', transfer: result });
    } catch (err) {
      dispatch({ type: 'error', message: friendlyApiError(err, 'Transfer başarısız') });
    }
  }

  async function loadMerchantMaster(): Promise<string | null> {
    try {
      const { merchant } = await api.getMerchant();
      const addr = merchant.masterWallets[state.chainId];
      if (addr) {
        setMerchantMaster(addr);
        return addr;
      }
      return null;
    } catch {
      return null;
    }
  }

  return (
    <div className="cp-widget" role="main">
      <DevModeBanner provider={auth.mode} />
      <DebugBanner
        info={{
          apiBaseUrl: env.apiBaseUrl,
          apiKey: env.apiKey,
          web3AuthClientId: env.web3AuthClientId,
          forceDevWallet: env.forceDevWallet,
          chainId: env.chainId,
          tokenSymbol: env.tokenSymbol,
          fiatCurrency: env.fiatCurrency,
          isProduction: env.isProduction,
        }}
      />
      {restoring ? (
        <div style={{ padding: '32px 20px', textAlign: 'center', color: '#94a3b8' }}>
          Oturum yükleniyor…
        </div>
      ) : null}
      {!restoring && state.step === 'anonymous' && <Login onLogin={handleLogin} />}

      {state.auth && state.step !== 'transfer-confirmed' && (
        <>
          <Balance
            address={state.auth.address}
            email={state.auth.email}
            balance={state.balance}
            decimals={token.decimals}
            symbol={state.tokenSymbol}
            loading={balanceLoading}
            onRefresh={refreshBalance}
          />
          <OnRampButton onClick={handleOnRamp} />
          {state.balance > 0n && merchantMaster ? (
            <TransferButton
              destination={merchantMaster}
              amountLabel={`${formatTokenAmount(state.balance, token.decimals)} ${state.tokenSymbol}`}
              onClick={handleTransfer}
              disabled={state.step === 'transfer-pending'}
            />
          ) : null}
        </>
      )}

      {state.step === 'transfer-confirmed' && state.lastTransfer ? (
        <Receipt
          transfer={state.lastTransfer}
          explorerBaseUrl={chain.blockExplorer}
          onReset={() => dispatch({ type: 'logout' })}
        />
      ) : null}

      {state.errorMessage ? (
        <div className="cp-error" role="alert">
          {state.errorMessage}{' '}
          <button
            className="cp-button cp-secondary"
            style={{ width: 'auto', padding: '4px 10px', marginLeft: 8 }}
            onClick={() => dispatch({ type: 'clear-error' })}
          >
            Kapat
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Translate the raw failures the API client throws into something a
 * non-technical user can act on. The most common case during the
 * widget-before-backend phase is Vercel returning 404/405 for the
 * `/v1/*` paths because the dev-server isn't up yet — surface that
 * as a real explanation instead of "Request failed: 405".
 */
function friendlyApiError(err: unknown, prefix: string): string {
  if (err instanceof ApiError) {
    if (err.code === 'NO_BASE_URL') {
      return `${prefix}: ${err.message}`;
    }
    if (err.status === 404 || err.status === 405) {
      return `${prefix}: API uç noktası bulunamadı. VITE_API_BASE_URL yanlış yapılandırılmış olabilir (sayfaya ?debug=1 ekleyip değeri doğrula).`;
    }
    if (err.status === 401 || err.status === 403) {
      return `${prefix}: Yetki reddedildi. Merchant API key (VITE_MERCHANT_API_KEY) ayarlandı mı?`;
    }
    return `${prefix}: ${err.message}`;
  }
  if (err instanceof TypeError && /fetch|network|failed/i.test(err.message)) {
    return `${prefix}: Backend'e ulaşılamadı. İnternet bağlantısı veya VITE_API_BASE_URL yapılandırması.`;
  }
  return `${prefix}: ${err instanceof Error ? err.message : 'bilinmeyen hata'}`;
}
